import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useSelector } from "react-redux";

import PagingControls from "@cayuse/shared-react.components.paging-controls";
import { PreventLeaveDialog } from "@cayuse/shared-react.components.prevent-leave-page-dialog";
import { isNonEmptyArray, isNonEmptyString } from "@cayuse/shared-react.utils.type-checks";
import { Loader } from "semantic-ui-react";

import { Combobox } from "@cayuse/shared-react.components.form.combobox";
import FileUpload from "@cayuse/shared-react.components.form.inputs.file-upload";
import FormattedDatetime from "@cayuse/shared-react.components.formatted-datetime";
import IconButton from "@cayuse/shared-react.components.icon-button";
import VerticalSpacing from "@cayuse/shared-react.components.vertical-spacing";


import ConfirmationModal from "components/ConfirmationModal";
import Table from "components/Table";
import { COLUMN_HEADER_KEYS, DEFAULT_PAGE_LIMIT, FLOWABLE_BASE_URL } from "globals/constants";
import { useDictionary } from "hooks";
import { attachments as attachmentsApi } from "services/api/attachments";
import { shellSelectors } from "store/selectors";
import {
	sortCollectionByKey,
	spUrl,
	findUsers,
	notifyError,
	notifySuccess
} from "utils";
import { put, downloadFile, request, downloadMultipartFile, save } from "utils/proxy";

import { validate as uuidValidate } from "uuid";

import Pager from "../Pager";
import styles from "./styles.css";


export const MAX_FILE_SIZE_75_MB = 75 * Math.pow(1024, 2);

const FormAttachmentsView = ({
	formInstance,
	fullName,
	attachmentTypes,
	attachmentsUrl,
	tabsRef,
	formTabText,
	canAddAttachments,
	canEditAttachments,
	setJustActivatedSectionLabel,
	isUploading,
	setIsUploading,
}) =>{
	const [ attachmentType, setAttachmentType ] = useState();
	const [ attachments, setAttachments ] = useState([]);
	const [ isConfirmDeleteOpen, setIsConfirmDeleteOpen ] = useState(false);
	const hasAttachments = isNonEmptyArray(attachments);
	const hasAttachmentType = !!attachmentType && attachmentType?.value;
	const [ isLoadingAttachments, setIsLoadingAttachments ] = useState(true);
	const [ sortOrder, setSortOrder ] = useState();
	const [ updateAttachments, setUpdateAttachments ] = useState(false);
	const [ updateNames, setUpdateNames ] = useState(false);
	const [ userNames, setUserNames ] = useState([]);

	const [ itemsPerPage, setItemsPerPage ] = useState(DEFAULT_PAGE_LIMIT);
	const [ pageNumber, setPageNumber ] = useState(0);
	const [ numberOfPages, setNumberOfPages ] = useState(0);
	const [ totalAttachments, setTotalAttachments ] = useState(0);
	const [ reloadNotes, setReloadNotes ] = useState(true);
	const [ attachmentModal, setAttachmentModal ] = useState({
		attachmentIndex: 0,
		isVisible: false
	});

	const { personId } = useSelector(shellSelectors.user);

	const dictionary = useDictionary("proposals.attachments"); //TODO: change dictionary!

	const isProject =  formInstance.type === COLUMN_HEADER_KEYS.PROJECT;

	const getAttachmentTypeOptions = useMemo(() => {
		return attachmentTypes
			.filter(attachment => attachment.active) //TODO:remove hardcoded instance when awards get attachments
			.map(attachment => ({
				key: !!attachment.hash ? attachment.hash : attachment.value,
				value: attachment.value,
				label: attachment.value
			}));
	}, [ attachmentTypes ]);

	const handleDownload = useCallback((attachmentSource, filePath, fileName) => {
		if (filePath?.startsWith("platform://")) {
			downloadMultipartFile({
				id: filePath.slice(11),
				fileName
			});
		}
		else if (attachmentSource === "Attachment Tab") {
			downloadFile(`${FLOWABLE_BASE_URL}/cayuse-api/attachments?filePath=${filePath}`, fileName);
		}
		else {
			downloadFile(`${FLOWABLE_BASE_URL}/cayuse-api/${filePath}`, fileName);
		}
	}, []);

	const handleOnUpload = async acceptedFiles => {
		setIsUploading(true);
		const uploadPromises = [];
		const exceedSizeFiles = [];
		const fileExtensionErrors = [];
		let isError = false;

		const up = async file => {
			const associateUrl = `${attachmentsUrl}?attachmentType=${encodeURIComponent(attachmentType?.value)}`;
			const fileMetadata = await attachmentsApi.upload(file);

			if (!fileMetadata) {
				const error = new Error("Failed to upload file. Please try again.");
				notifyError(error.message, error);
				isError = true;

				return;
			}
			const callback = async () => {
				const response = await save(associateUrl, fileMetadata);

				return { ...response, fullName };
			};

			return callback;
		};

		try {
			for (const file of acceptedFiles) {
				const fileExtension = file.name.split(".").reverse()[0].toLowerCase();

				if (fileExtension === "exe") {
					fileExtensionErrors.push(file);
				} else if (MAX_FILE_SIZE_75_MB && file.size != null && file.size > MAX_FILE_SIZE_75_MB) {
					exceedSizeFiles.push(file);
				} else {
					uploadPromises.push(up(file));
				}
			}

			if (fileExtensionErrors.length > 0) {
				const error = new Error("Attachments with filetype .exe cannot be accepted. Please try a different file type.");
				notifyError(error.message, error);
				isError = true;
			}
			if (acceptedFiles.length === 1 && exceedSizeFiles.length === 1) {
				const error = new Error("The uploaded file exceeds the 75 MB limit.");
				notifyError(error.message, error);
				isError = true;
			} else if (acceptedFiles.length > 1 && exceedSizeFiles.length >= 1) {
				const error = new Error(`${exceedSizeFiles.length} of the ${acceptedFiles.length} uploaded files exceed the 75 MB limit.`);
				notifyError(error.message, error);
				isError = true;
			}

			const callbacks = await Promise.all(uploadPromises);
			const newAttachments = [];
			for (const callback of callbacks) {
				const response = await callback();
				newAttachments.push(response);
			}
			setAttachments([ ...newAttachments, ...attachments ]);
			setReloadNotes(true);

			if (!isError) {
				notifySuccess(
					`Successfully uploaded new attachments!`
				);
			}
		}
		catch (e) {
			notifyError(e.message, e);
		}
		finally {
			setIsUploading(false);
		}
	};

	const handleConfirmDelete = () => {
		const { attachmentId, fileName, attachmentType } = attachments[attachmentModal.attachmentIndex];
		const payload = {
			fileName,
			attachmentType
		};

		put(`${attachmentsUrl}/${attachmentId}`, payload)
			.then(() => {
				const mutableAttachments = [ ...attachments ];
				mutableAttachments.splice(attachmentModal.attachmentIndex, 1);
				setAttachments(mutableAttachments);
				setTotalAttachments(totalAttachments - 1);
				notifySuccess(
					`Successfully deleted ${returnAttachmentTitle(attachmentModal.attachmentIndex)} attachment!`
				);
			})
			.catch(e => {
				const message = dictionary.errors.unableToSave || "Unable to complete the download";
				notifyError(message, e);
			});
		handleIsConfirmDeleteOpen();
	};

	const handleIsConfirmDeleteOpen = index => {
		setAttachmentModal({ ...attachmentModal, attachmentIndex: isConfirmDeleteOpen ? 0 : index });
		setIsConfirmDeleteOpen(!isConfirmDeleteOpen);
	};

	const handleSort = () => {
		sortCollectionByKey(attachments, "fullName");

		const isDescending = sortOrder === "descending";
		const updatedAttachments = isDescending || !sortOrder
			? [ ...attachments ]
			: [ ...attachments ].reverse();

		setAttachments(updatedAttachments);
		setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
	};

	const returnAttachmentTitle = index => {
		return hasAttachments
			? attachments[index]?.fileName?.length > 70
				? `${attachments[index].fileName.slice(0, 70)}...`
				: attachments[index].fileName
			: null;
	};

	const handleFindFormElement = useCallback(event => {
		setJustActivatedSectionLabel(event.target.innerHTML);

		if (tabsRef.current) {
			const tabs = tabsRef.current.querySelector("ul[role='tablist']").childNodes;
			for (const tab of tabs) {
				const tabButton = tab.childNodes[0];
				if (tabButton.textContent === formTabText) {
					tabButton.dispatchEvent(new Event("click", { bubbles: true }));
					break;
				}
			}
		}
	}, [ formTabText, setJustActivatedSectionLabel, tabsRef ]);

	const handleFindUsers = useCallback(() => {
		const userIdsToFetchHash = {};
		const names = [ ...userNames ];
		const findExistingNameById = id => names.find(({ nameId }) => id === nameId);
		attachments.forEach(({ attachmentSource, uploadedBy }) => {
			if (!findExistingNameById(uploadedBy)) {
				userIdsToFetchHash[uploadedBy] = { value: true, idType: attachmentSource === "Attachment Tab" ? "personId" : "userAccountId" };
			}
		});
		const userIds = Object.keys(userIdsToFetchHash);
		const hasIdsToFetch = isNonEmptyArray(userIds);
		if (hasIdsToFetch) {
			Promise.all(userIds.map(userId => {
				return findUsers({
					pageLimit: 100,
					pageOffset: 0,
					searchTerms: [
						{ key: userIdsToFetchHash[userId].idType, val: userId },
					],
					includeInactive: true,
				})
					.then(response => {
						const person = response.find(person => person[userIdsToFetchHash[userId].idType] === userId);

						return names.push({
							name: isNonEmptyString(person?.fullName)
								? person.fullName
								: (uuidValidate(userId) ? (dictionary.unknownUser || "Unknown User") : userId),
							nameId: userId,
						});
					})
					.catch(error => {
						notifyError("Something went wrong. Please try again.", error);
					});
			}))
				.finally(() => {
					setUserNames(names);

					setUpdateAttachments(true);
				});
		}else{
			setUpdateAttachments(true);
		}
	}, [ attachments, dictionary.unknownUser, userNames ]);

	useEffect(() => {
		if(reloadNotes && formInstance.instanceId) {
			request(spUrl(`${formInstance.endpoint}/${formInstance.instanceId}/attachments?page=${pageNumber}&size=${itemsPerPage}`)).then(data => {
				const attachments = isProject ? data.content : data;
				setTotalAttachments(isProject ? data.totalElements : data.length);

				setAttachments(Pager.pageSlice(attachments, itemsPerPage, isProject ? 0 : pageNumber));

				if (isNonEmptyArray(attachments)) {
					setUpdateNames(true);
					setIsLoadingAttachments(true);
				} else {
					setUpdateNames(false);
					setIsLoadingAttachments(false);
				}
				setReloadNotes(false);
			});
		}
	}, [ formInstance.instanceId, formInstance.endpoint, reloadNotes, isProject, itemsPerPage, pageNumber ]);

	useEffect(() => {
		if(hasAttachments && updateNames) {
			setUpdateNames(false);
			handleFindUsers();
		}

		if(updateAttachments) {
			const updatedAttachments = attachments.map(attachment => {
				return {
					...attachment,
					fullName: userNames?.find(name => name.nameId === attachment.uploadedBy)?.name
				};
			});
			setAttachments(updatedAttachments);
			setUpdateAttachments(false);
			setIsLoadingAttachments(false);
		}
	}, [
		attachments,
		dictionary.unknownUser,
		handleFindUsers,
		hasAttachments,
		updateAttachments,
		updateNames,
		userNames
	]);

	const loadNextPage = nextPageNumber => {
		nextPageNumber--;
		setPageNumber(nextPageNumber);
		setReloadNotes(true);
	};

	const updatePageSize = itemsPerPage => {
		const numberOfPages = Math.ceil(attachments / itemsPerPage);
		setItemsPerPage(itemsPerPage);
		setNumberOfPages(numberOfPages);
		setReloadNotes(true);
	};

	return(
		<>
			<PreventLeaveDialog prevent={isUploading} />
			{canAddAttachments &&
				<div>
					<VerticalSpacing>
						<VerticalSpacing>
							<h3 className={`${styles.label} ${styles.bold}`}>{dictionary.instructions.title || "Add Attachment"}</h3>
							<p className={styles.instructionsText}>{dictionary.instructions.text}</p>
						</VerticalSpacing>
						<VerticalSpacing>
							<div className={styles.attachmentTypeWrapper}>
								<Combobox
									onChange={option => setAttachmentType(option)}
									options={getAttachmentTypeOptions}
									placeholder={dictionary.attachmentTypeSelector.placeholder}
									value={attachmentType}
									label={
										dictionary.attachmentTypeSelector.label || "Attachment Type"
									}
									components={{
										StartIcon: (
										<svg
											aria-hidden="true"
											className="svg-inline--fa fa-magnifying-glass icon solid"
											xmlns="http://www.w3.org/2000/svg"
											viewBox="0 0 512 512"
										>
											<path
											fill="currentColor"
											d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376c-34.4 25.2-76.8 40-122.7 40C93.1 416 0 322.9 0 208S93.1 0 208 0S416 93.1 416 208zM208 352a144 144 0 1 0 0-288 144 144 0 1 0 0 288z"
											/>
										</svg>
										),
										DropdownIcon: (
										<svg
											height="20"
											width="20"
											viewBox="0 0 20 20"
											aria-hidden="true"
											focusable="false"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
											fill="currentColor"
											d="M4.516 7.548c0.436-0.446 1.043-0.481 1.576 0l3.908 3.747 3.908-3.747c0.533-0.481 1.141-0.446 1.574 0 0.436 0.445 0.408 1.197 0 1.615-0.406 0.418-4.695 4.502-4.695 4.502-0.217 0.223-0.502 0.335-0.787 0.335s-0.57-0.112-0.789-0.335c0 0-4.287-4.084-4.695-4.502s-0.436-1.17 0-1.615z"
											/>
										</svg>
										),
										ClearIcon: (
										<svg
											height="20"
											width="20"
											viewBox="0 0 20 20"
											aria-hidden="true"
											focusable="false"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path d="M14.348 14.849c-0.469 0.469-1.229 0.469-1.697 0l-2.651-3.030-2.651 3.029c-0.469 0.469-1.229 0.469-1.697 0-0.469-0.469-0.469-1.229 0-1.697l2.758-3.15-2.759-3.152c-0.469-0.469-0.469-1.228 0-1.697s1.228-0.469 1.697 0l2.652 3.031 2.651-3.031c0.469-0.469 1.228-0.469 1.697 0s0.469 1.229 0 1.697l-2.758 3.152 2.758 3.15c0.469 0.469 0.469 1.229 0 1.698z" />
										</svg>
										),
									}}
								/>
							</div>
						</VerticalSpacing>
					</VerticalSpacing>
					<VerticalSpacing>
						{ isUploading ? (
							<div className="dropzone" style={{ minHeight: 146 }}>
								<Loader active size="small" inline style={{ marginBottom: 10 }} />
								<span>Uploading attachment...</span>
							</div>
						) : (
							<FileUpload onDrop={handleOnUpload} disabled={!hasAttachmentType}/>
						)}
					</VerticalSpacing>
				</div>
			}
			{isConfirmDeleteOpen &&
				<ConfirmationModal
					icon="file-alt"
					visible={isConfirmDeleteOpen}
					title={dictionary.confirmationModal.title || "Delete Attachment" }
					message={returnAttachmentTitle(attachmentModal.attachmentIndex)}
					onConfirm={handleConfirmDelete}
					onCancel={handleIsConfirmDeleteOpen}
				/>
			}
			<div>
				<VerticalSpacing>
					<Table isLoading={isLoadingAttachments} sortable captionText="Form Attachments">
						<Table.Header>
							<Table.Row>
								<Table.HeaderCell>{dictionary.columnHeaders?.attachmentName || "Attachment"}</Table.HeaderCell>
								<Table.HeaderCell>{dictionary.columnHeaders?.attachmentType || "Attachment Type"}</Table.HeaderCell>
								<Table.HeaderCell>{dictionary.columnHeaders?.date || "Uploaded"}</Table.HeaderCell>
								<Table.HeaderCell sorted={sortOrder} onClick={handleSort}>{dictionary.columnHeaders?.uploadedBy || "By"}</Table.HeaderCell>
								<Table.HeaderCell>{dictionary.columnHeaders?.location || "Location"}</Table.HeaderCell>
							</Table.Row>
						</Table.Header>

						<Table.Body>
							{hasAttachments
								? (attachments.map(({
									uploadedBy,
									uploadedOn,
									attachmentSource,
									attachmentType,
									fileName,
									filePath,
									fullName,
								}, index) => (
									<Table.Row key={index}>
										<Table.Cell>
											<button
												className={styles.link}
												onClick={() => handleDownload(attachmentSource, filePath, fileName)}
												aria-label={`Download ${fileName} file`}
											>
												{returnAttachmentTitle(index)}
											</button>
										</Table.Cell>
										<Table.Cell>
											{attachmentType}
										</Table.Cell>
										<Table.Cell>
											<FormattedDatetime date={uploadedOn} />
										</Table.Cell>
										<Table.Cell>{fullName}</Table.Cell>
										<Table.Cell className={styles.location}>
											{attachmentSource === "Attachment Tab"
												? attachmentSource
												: (
													<button
														onClick={handleFindFormElement}
														className={styles.link}
														aria-label={`Open ${fileName} file on ${formTabText} page`}
													>
														{attachmentSource}
													</button>
												)}
											{(canEditAttachments || personId === uploadedBy) && attachmentSource === "Attachment Tab" &&
												<IconButton
													className={styles.delete}
													aria-label={`Delete ${fileName} file`}
													icon="trash"
													size="bigger"
													onClick={() => handleIsConfirmDeleteOpen(index)}
													tertiary
												/>
											}
										</Table.Cell>
									</Table.Row>
								)))
								: ""
							}
						</Table.Body>
					</Table>
				</VerticalSpacing>
				<div>
					<PagingControls
						items={attachments}
						onPageChange={loadNextPage}
						totalItems={totalAttachments}
						pageLimit={itemsPerPage}
						pageOffset={pageNumber}
						totalPages={numberOfPages}
						onPageSizeChange={updatePageSize}
					/>
				</div>
			</div>
		</>
	);
};

export default FormAttachmentsView;
